import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Keep the existing storage key for compatibility, but make the bright Hola Pay surface the default.
    const savedTheme = localStorage.getItem('alphabit-theme');
    return savedTheme || 'light';
  });

  useEffect(() => {
    // Save to localStorage whenever theme changes
    localStorage.setItem('alphabit-theme', theme);
    
    // Apply theme class to document root
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark, isLight }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme color constants for consistency
export const themeColors = {
  dark: {
    bg: 'bg-[#23282B]',
    bgSection: 'bg-[#1C2022]',
    bgCard: 'bg-[#2B3033]',
    bgGradient: 'bg-[#23282B]',
    text: 'text-white',
    textMuted: 'text-gray-400',
    textSubtle: 'text-gray-300',
    border: 'border-white/15',
    borderHover: 'hover:border-[#F5D547]/60',
    accent: 'text-[#F5D547]',
    accentBg: 'bg-[#F5D547]',
    cardBg: 'bg-[#2B3033]',
    inputBg: 'bg-white/5',
    inputBorder: 'border-white/20',
    inputFocus: 'focus:border-[#F5D547]',
    navBg: 'bg-[#23282B]',
    navBorder: 'border-white/15',
    shadow: 'shadow-black/20',
    glowShadow: 'shadow-[0_8px_30px_rgba(0,0,0,0.18)]',
  },
  light: {
    bg: 'bg-[#FBFAF3]',
    bgSection: 'bg-[#FFFDF5]',
    bgCard: 'bg-[#FFFDF5]',
    bgGradient: 'bg-[#FBFAF3]',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    textSubtle: 'text-slate-600',
    border: 'border-[#D8D4C7]',
    borderHover: 'hover:border-[#23282B]',
    accent: 'text-[#23282B]',
    accentBg: 'bg-[#F5D547]',
    cardBg: 'bg-white shadow-lg',
    inputBg: 'bg-[#FFFDF5]',
    inputBorder: 'border-[#D8D4C7]',
    inputFocus: 'focus:border-[#00736F]',
    navBg: 'bg-[#FFFDF5]',
    navBorder: 'border-[#D8D4C7]',
    shadow: 'shadow-black/10',
    glowShadow: 'shadow-lg shadow-black/10',
  }
};

// Helper hook to get current theme colors
export const useThemeColors = () => {
  const { isDark } = useTheme();
  return isDark ? themeColors.dark : themeColors.light;
};
